import { DEFAULT_HANGING_PROTOCOLS } from '@/data/defaultHangingProtocols';
import { viewerSettingsService } from '@/lib/settings/viewerSettingsService';
/**
 * Legacy localStorage keys used before hanging protocols were folded into
 * viewer_settings. Reads remain as a fallback during a migration window so
 * users with stale localStorage data are not reset to defaults; writes go
 * through viewerSettingsService.
 */
export const HANGING_PROTOCOLS_STORAGE_KEY = 'nl_hanging_protocols';
export const HANGING_PROTOCOLS_ENABLED_KEY = 'nl_hanging_protocols_enabled';
/**
 * One-shot copy of any legacy localStorage values into viewer_settings.
 * Triggered by the presence of legacy keys in localStorage; the keys are
 * deleted after a successful migration, so the function is naturally
 * idempotent on subsequent calls.
 */
function migrateLegacyHangingProtocolsIfNeeded() {
    if (typeof window === 'undefined')
        return;
    try {
        const protocolsRaw = window.localStorage.getItem(HANGING_PROTOCOLS_STORAGE_KEY);
        const enabledRaw = window.localStorage.getItem(HANGING_PROTOCOLS_ENABLED_KEY);
        if (protocolsRaw === null && enabledRaw === null)
            return;
        let migratedProtocols;
        if (protocolsRaw !== null) {
            try {
                const parsed = JSON.parse(protocolsRaw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    migratedProtocols = parsed;
                }
            }
            catch {
                // Fall through; corrupt legacy data simply gets dropped.
            }
        }
        const migratedEnabled = enabledRaw !== null ? enabledRaw !== 'false' : undefined;
        if (migratedProtocols !== undefined || migratedEnabled !== undefined) {
            viewerSettingsService.updateSettings({
                ...(migratedProtocols ? { hangingProtocols: migratedProtocols } : {}),
                ...(migratedEnabled !== undefined ? { hangingProtocolsEnabled: migratedEnabled } : {}),
            });
        }
        window.localStorage.removeItem(HANGING_PROTOCOLS_STORAGE_KEY);
        window.localStorage.removeItem(HANGING_PROTOCOLS_ENABLED_KEY);
    }
    catch {
        // Migration is best-effort; leave defaults in place on storage failures.
    }
}
export function isHangingProtocolsEnabled() {
    if (typeof window === 'undefined')
        return true;
    migrateLegacyHangingProtocolsIfNeeded();
    return viewerSettingsService.getSettings().hangingProtocolsEnabled ?? true;
}
export function setHangingProtocolsEnabled(enabled) {
    if (typeof window === 'undefined')
        return;
    viewerSettingsService.updateSettings({ hangingProtocolsEnabled: enabled });
}
export function setHangingProtocols(protocols) {
    if (typeof window === 'undefined')
        return;
    viewerSettingsService.updateSettings({ hangingProtocols: protocols });
}
const DEFAULT_SCORE_WEIGHTS = {
    species: 50,
    modality: 30,
    bodyPart: 20,
};
function safeLoadProtocolsFromStorage() {
    if (typeof window === 'undefined')
        return DEFAULT_HANGING_PROTOCOLS;
    migrateLegacyHangingProtocolsIfNeeded();
    const settings = viewerSettingsService.getSettings();
    if (settings.hangingProtocols?.length)
        return settings.hangingProtocols;
    return DEFAULT_HANGING_PROTOCOLS;
}
function normalizeBodyPart(value) {
    return value.trim().toLowerCase();
}
function hasBodyPartMatch(protocol, context) {
    const protocolParts = new Set(protocol.bodyParts.map(normalizeBodyPart));
    return context.bodyPartTokens.some((part) => protocolParts.has(normalizeBodyPart(part)));
}
function hasSpeciesMatch(protocol, context) {
    if (context.species.length === 0)
        return false;
    // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- a protocol and a study each name a few species
    return protocol.species.some((species) => context.species.includes(species));
}
function hasModalityMatch(protocol, modalities) {
    // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- a protocol and a study each name a few modalities
    return protocol.modality.some((m) => modalities.includes(m));
}
function scoreProtocol(protocol, context) {
    // Modality is a GATE, not a weight.
    //
    // Scored, species (50) + body part (20) clears the pass mark on its own, so
    // a study whose modality the protocol never mentions could still win. In
    // practice: normalizeModality only recognises DX/CR/CT/MR, so every
    // ultrasound, endoscopy and microscopy study has an EMPTY modality list, and
    // a canine abdominal ultrasound matched the canine ABDOMEN X-ray protocol -
    // hung in a radiograph layout, complete with its prior row. A protocol that
    // does not claim this modality has no business hanging the study.
    //
    // An empty modality list (nothing recognisable in the payload) also fails
    // the gate, which is the right way round: no evidence is not a licence to
    // hang the study as something else.
    if (!hasModalityMatch(protocol, context.modalities))
        return 0;
    let score = 0;
    if (hasSpeciesMatch(protocol, context)) {
        score += DEFAULT_SCORE_WEIGHTS.species;
    }
    score += DEFAULT_SCORE_WEIGHTS.modality;
    if (hasBodyPartMatch(protocol, context)) {
        score += DEFAULT_SCORE_WEIGHTS.bodyPart;
    }
    return score;
}
/**
 * The protocols that ship with the app. A saved copy keeps its seeded id, so the id set is the
 * one reliable way to tell a built-in from a protocol the user made (those get a
 * `proto-user-` id).
 */
const SEEDED_PROTOCOL_IDS = new Set(DEFAULT_HANGING_PROTOCOLS.map((p) => p.id));
export function isUserProtocol(p) {
    return !SEEDED_PROTOCOL_IDS.has(p.id);
}
export function loadHangingProtocols() {
    return structuredClone(safeLoadProtocolsFromStorage());
}
export function matchProtocol(context, protocols = loadHangingProtocols(), minScore = 60) {
    let best = null;
    protocols.forEach((protocol) => {
        const score = scoreProtocol(protocol, context);
        if (score < minScore)
            return;
        // On a tie a protocol the user made beats a built-in: user protocols are appended after
        // the built-ins, so first-wins alone would mean a reader's own Canine chest protocol never
        // hangs a canine chest, and the built-in can be neither reordered nor deleted.
        const beatsBest = !best ||
            score > best.score ||
            (score === best.score && isUserProtocol(protocol) && !isUserProtocol(best.protocol));
        if (beatsBest) {
            best = { protocol, score };
        }
    });
    return best;
}
